import { MessageRole } from "@/enums/mission";
import api from "@/modules/api";
import { sleep } from "@/modules/common";
import { useMate } from "@/stores/mate";
import { MateDispatchMessage } from "@/types/entity";
import { defineStore } from "pinia";

interface DispatchMessageState {
  dispatchUuid2MessageList: Record<string, { keyList: number[] }>; // 会话id对应的消息列表
  messageMap: Record<string, MateDispatchMessage>; // 消息id对应的消息
  showInsufficientCrystalPopup: boolean;
  showInsufficientPowerPopup: boolean;
  missCrystalAmount: number;
  missPowerAmount: number;
  prevGeneratedMsgId: number;
  replyOptions: string[];
  replyGenerating: boolean;
  fetchingFlag: boolean;
}

export const useMateDispatchMessage = defineStore("mateDispatchMessage", {
  state: (): DispatchMessageState => {
    return {
      dispatchUuid2MessageList: {},
      messageMap: {},
      showInsufficientCrystalPopup: false,
      showInsufficientPowerPopup: false,
      missCrystalAmount: 2,
      missPowerAmount: 20,
      prevGeneratedMsgId: 0,
      replyOptions: [],
      replyGenerating: false,
      fetchingFlag: false,
    };
  },
  getters: {
    messageListByDispatchUuid: (state) => (uuid: string) => {
      if (!state.dispatchUuid2MessageList[uuid]) {
        return [];
      }
      return state.dispatchUuid2MessageList[uuid].keyList.map(
        (messageId) => state.messageMap[messageId]
      );
    },
  },
  actions: {
    updateMap(msgList: MateDispatchMessage[]): MateDispatchMessage[] {
      return msgList.map((msg) => {
        const target = this.messageMap[msg.id];
        if (target) {
          Object.assign(target, msg);
        } else {
          this.messageMap[msg.id] = msg;
        }
        return this.messageMap[msg.id];
      });
    },
    getOrInitDispatchItem(dispatchUUID: string): {
      keyList: number[];
    } {
      if (!this.dispatchUuid2MessageList[dispatchUUID]) {
        this.dispatchUuid2MessageList[dispatchUUID] = {
          keyList: [] as number[],
        };
      }
      return this.dispatchUuid2MessageList[dispatchUUID];
    },
    async fetchMessageList(
      dispatchUUID: string,
      pageSize = 6,
      cursor?: string
    ) {
      const res = await api.readMateDispatchMessagePage({
        pagination: { perPage: pageSize },
        filter: { dispatch: dispatchUUID },
        sort: { field: "mate_dispatch_message.id", order: "desc" },
        cursor,
      });
      this.updateMap(res.page.data);
      return res.page.data;
    },
    async initMessageList(dispatchUUID: string) {
      const sessionItem = this.getOrInitDispatchItem(dispatchUUID);
      const messages = await this.fetchMessageList(dispatchUUID, 20);
      sessionItem.keyList = messages.map((msg) => msg.id);
    },
    async sendMessage(
      params: Parameters<typeof api.createOneMateDispatchMessage>[0]
    ): Promise<ReturnType<typeof api.createOneMateDispatchMessage>> {
      return await api.createOneMateDispatchMessage(params);
    },
    async getNewestMessage(
      dispatchUUID: string,
      withDelay = false,
      cb: () => void = () => {}
    ): Promise<number> {
      const sessionItem = this.getOrInitDispatchItem(dispatchUUID);
      const messageList = this.messageListByDispatchUuid(dispatchUUID);
      let cursor: undefined | string = undefined;
      if (messageList.length > 0) {
        cursor = this.useCursorEncode(messageList[0].id, false);
      }
      const messages = await this.fetchMessageList(dispatchUUID, 10, cursor);
      if (messages.length === 0) {
        return 0;
      }
      const newMessages = messages.filter(
        (msg) => !sessionItem.keyList.includes(msg.id)
      );
      if (withDelay) {
        for (let i = newMessages.length - 1; i >= 0; i--) {
          const item = newMessages[i];
          if (item.role !== MessageRole.Text) {
            await sleep(Math.random() * 1000 + 1000);
          }
          sessionItem.keyList.unshift(item.id);
          await cb();
        }
      } else {
        sessionItem.keyList.unshift(...newMessages.map((msg) => msg.id));
      }
      return newMessages.length;
    },
    useCursorEncode(id: number, pointsToNextItems = false): string {
      return btoa(
        JSON.stringify({
          "mate_dispatch_message.id": id,
          _pointsToNextItems: pointsToNextItems,
        })
      );
    },
    async fetchOlderMessageList(
      dispatchUUID: string
    ): Promise<{ hasMore: boolean; count: number }> {
      const sessionItem = this.getOrInitDispatchItem(dispatchUUID);
      const messageList = this.messageListByDispatchUuid(dispatchUUID);
      let cursor = undefined;
      if (messageList.length !== 0) {
        cursor = this.useCursorEncode(
          messageList[messageList.length - 1].id,
          true
        );
      }
      const messages = await this.fetchMessageList(dispatchUUID, 10, cursor);
      if (messages.length === 0) {
        return { hasMore: false, count: 0 };
      }
      sessionItem.keyList.push(...messages.map((msg) => msg.id));

      return { hasMore: true, count: messages.length };
    },
    getLatestReplyByDispatchUuid(
      uuid: string
    ): MateDispatchMessage | undefined {
      const messageList = this.dispatchUuid2MessageList[uuid].keyList.map(
        (messageId) => this.messageMap[messageId]
      );
      if (messageList.length === 0) {
        return;
      }
      const userStore = useMate();
      return messageList.find(
        (msg) => msg.mateId !== userStore.currentMate?.id
      );
    },
    getMsgByIdx(uuid: string, idx: number): MateDispatchMessage | undefined {
      const msgId = this.dispatchUuid2MessageList[uuid]?.keyList[idx] ?? null;
      if (!msgId) return;
      return this.messageMap[msgId];
    },
  },
});
